/**
 * @fileoverview Theory drawer — the 4C/ID "supportive information" component.
 * Always-available domain knowledge organised by topic, with formulas
 * (LatexRenderer) and small inline diagrams.
 *
 * @module HydraulicBalancingTesting/components/TheoryDrawer
 */

import React from 'react';
import { Drawer, Collapse } from 'antd';

import { LatexRenderer } from '../../../Shared/LatexRenderer';
import { THEORY_TOPICS } from '../content';
import { COLORS } from '../constants';

import styles from '../HydraulicBalancingTesting.module.css';

// ----------------------------------------------------------------------------
// Inline SVG diagrams
// ----------------------------------------------------------------------------

/** Pump curve vs system curves (balanced/unbalanced). */
const PumpCurveDiagram = () => {
  const W = 320;
  const H = 180;
  const padL = 36;
  const padB = 28;
  const x = (f) => padL + (W - padL - 10) * f;
  const y = (f) => 10 + (H - padB - 10) * (1 - f);

  // pump curve: dp = 1 - q^2 ; system curves dp = k q^2
  const pump = [];
  const sysSteep = [];
  const sysFlat = [];
  for (let q = 0; q <= 1; q += 0.05) {
    pump.push(`${x(q)},${y(1 - q * q)}`);
    if (2.6 * q * q <= 1.05) sysSteep.push(`${x(q)},${y(2.6 * q * q)}`);
    if (0.9 * q * q <= 1.05) sysFlat.push(`${x(q)},${y(0.9 * q * q)}`);
  }

  return (
    <svg width={W} height={H}>
      <line x1={padL} y1={10} x2={padL} y2={H - padB} stroke={COLORS.BORDER} />
      <line x1={padL} y1={H - padB} x2={W - 6} y2={H - padB} stroke={COLORS.BORDER} />
      <text x={8} y={16} fontSize={10} fill={COLORS.GRAY}>Δp</text>
      <text x={W - 16} y={H - 12} fontSize={10} fill={COLORS.GRAY}>V̇</text>
      <polyline points={pump.join(' ')} fill="none" stroke={COLORS.PRIMARY} strokeWidth={2} />
      <text x={x(0.04)} y={y(1) + 12} fontSize={10} fill={COLORS.PRIMARY}>pump curve</text>
      <polyline points={sysSteep.join(' ')} fill="none" stroke={COLORS.HOT} strokeWidth={2} />
      <text x={x(0.46)} y={y(0.83)} fontSize={10} fill={COLORS.HOT}>throttled network</text>
      <polyline points={sysFlat.join(' ')} fill="none" stroke={COLORS.GOOD} strokeWidth={2} />
      <text x={x(0.66)} y={y(0.38)} fontSize={10} fill={COLORS.GOOD}>open network</text>
      {/* operating points: 1-q² = kq² → q = 1/√(1+k) */}
      <circle cx={x(1 / Math.sqrt(3.6))} cy={y(2.6 / 3.6)} r={4} fill={COLORS.HOT} />
      <circle cx={x(1 / Math.sqrt(1.9))} cy={y(0.9 / 1.9)} r={4} fill={COLORS.GOOD} />
      <text x={padL + 4} y={H - 6} fontSize={9} fill={COLORS.GRAY}>
        The system always runs where the curves intersect.
      </text>
    </svg>
  );
};

/** Valve authority illustration. */
const AuthorityDiagram = () => {
  const W = 320;
  const H = 120;
  return (
    <svg width={W} height={H}>
      {/* branch with pressure-drop bar */}
      <text x={10} y={16} fontSize={10} fill={COLORS.GRAY}>Pressure drop along one branch:</text>
      {/* good authority */}
      <rect x={10} y={28} width={120} height={16} fill={COLORS.SECONDARY} />
      <rect x={130} y={28} width={140} height={16} fill={COLORS.BORDER} />
      <text x={10} y={58} fontSize={10} fill={COLORS.SECONDARY}>valve Δp (46%) — good authority ✓</text>
      {/* poor authority */}
      <rect x={10} y={72} width={36} height={16} fill={COLORS.HOT} />
      <rect x={46} y={72} width={224} height={16} fill={COLORS.BORDER} />
      <text x={10} y={102} fontSize={10} fill={COLORS.HOT}>valve Δp (13%) — pipes dominate, valve barely controls ✗</text>
    </svg>
  );
};

const DIAGRAMS = {
  pumpCurve: PumpCurveDiagram,
  authority: AuthorityDiagram,
};

// ----------------------------------------------------------------------------
// Block renderer
// ----------------------------------------------------------------------------

const Block = ({ block }) => {
  switch (block.type) {
    case 'text':
      return <p className={styles.theoryText}>{block.text}</p>;
    case 'latex':
      return (
        <div className={styles.theoryFormula}>
          <LatexRenderer latex={block.latex} />
        </div>
      );
    case 'list': {
      const items = block.items.map((item, i) => <li key={i}>{item}</li>);
      return block.ordered ? (
        <ol className={styles.theoryList}>{items}</ol>
      ) : (
        <ul className={styles.theoryList}>{items}</ul>
      );
    }
    case 'diagram': {
      const Diagram = DIAGRAMS[block.diagram];
      return Diagram ? (
        <div className={styles.theoryDiagram}>
          <Diagram />
        </div>
      ) : null;
    }
    default:
      return null;
  }
};

// ----------------------------------------------------------------------------
// Drawer
// ----------------------------------------------------------------------------

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {string|null} props.topicKey - topic to expand on open
 * @param {Function} props.onClose
 */
export const TheoryDrawer = ({ open, topicKey, onClose }) => (
  <Drawer
    title="Theory — how hydraulic balancing works"
    placement="right"
    width={672}
    open={open}
    onClose={onClose}
  >
    <Collapse
      accordion
      activeKey={undefined}
      defaultActiveKey={topicKey || THEORY_TOPICS[0].key}
      key={topicKey || 'default'}
      items={THEORY_TOPICS.map((topic) => ({
        key: topic.key,
        label: <b>{topic.title}</b>,
        children: topic.blocks.map((block, i) => <Block key={i} block={block} />),
      }))}
    />
  </Drawer>
);
